import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { ArrowRight, FileText, BookOpen, Sparkles } from "lucide-react";

const KEYWORDS = {
  1: "Initiator", 2: "Companion", 3: "Expressor", 4: "Builder",
  5: "Explorer", 6: "Nurturer", 7: "Seeker", 8: "Steward",
  9: "Humanitarian", 11: "Illuminator", 22: "Master Builder",
  33: "Compassionate Teacher",
};

const PACKAGE_PREVIEWS = {
  basic: {
    title: "Basic Report",
    pages: "5–6 pages",
    price: "₹120",
    icon: FileText,
    description:
      "A concise, warmly written introduction to your core numbers — ideal for a first reading.",
    sections: [
      "Personalized Welcome",
      "Introduction and Methodology",
      "Individual Number Interpretations",
      "Integrated Profile",
      "Strengths and Growth Areas",
      "Personal Year Analysis",
      "Reflection Questions",
      "Personalized Summary",
    ],
  },
  complete: {
    title: "Complete Report",
    pages: "10–12 pages",
    price: "₹299",
    icon: BookOpen,
    description:
      "A deep, chapter-length reading that explores interactions between your numbers across career, wealth, relationships and the year ahead.",
    sections: [
      "Personalized Welcome",
      "Introduction and Methodology",
      "Individual Number Interpretations",
      "Integrated Personality and Life Themes",
      "Strengths and Growth Areas",
      "Number Interactions",
      "Career and Work Themes",
      "Wealth and Money Reflection",
      "Relationships and Communication",
      "Personal Year Analysis",
      "Practical Reflection Exercises",
      "Personalized Synthesis and Key Takeaways",
    ],
  },
  name: {
    title: "Name Analysis",
    pages: "7–9 pages",
    price: "₹499",
    icon: Sparkles,
    description:
      "A dedicated name reading with a verified spelling comparison table, candidate commentary, and professional identity reflection.",
    sections: [
      "Personalized Welcome",
      "Introduction and Methodology",
      "Your Name Numbers Explained",
      "Name Number Patterns and Interactions",
      "Alternative Spelling Comparison",
      "Name and Birth-Date Interplay",
      "Communication and Professional Identity",
      "Reflection Questions",
      "Personalized Summary",
    ],
  },
};

export default function SampleReport() {
  const [sample, setSample] = useState(null);
  const [active, setActive] = useState("complete");

  useEffect(() => {
    api.get("/sample-report").then((r) => setSample(r.data)).catch(() => {});
  }, []);

  const pkg = PACKAGE_PREVIEWS[active];
  const Icon = pkg.icon;

  return (
    <>
      <Seo title="Sample Report" path="/sample-report" />
      <PageHeader
        eyebrow="Sample report"
        title="A glimpse of a finished Aetheria reading"
        subtitle="A worked example for a sample profile. Your own report is written specifically around your numbers and the areas you tell us matter to you — delivered as a premium, print-ready PDF."
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-16">
        {/* Preview cover card */}
        {sample && (
          <div
            data-testid="sample-report-preview-card"
            className="bg-white rounded-2xl border border-border overflow-hidden shadow-sm"
          >
            {/* Cover strip */}
            <div className="relative bg-navy text-ivory p-10 border-b-4 border-gold">
              <p className="eyebrow text-gold mb-3 tracking-widest">
                A Personalized Numerology Reading
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl leading-tight">
                {sample.name}
              </h2>
              <div className="mt-4 w-16 h-px bg-gold"></div>
              <p className="text-slate-300 text-sm mt-4">
                Born {sample.dob} · Pythagorean methodology
              </p>
            </div>

            {/* Core numbers table (mirrors PDF layout) */}
            <div className="p-8">
              <p className="eyebrow text-gold mb-2 tracking-widest">
                Your Reading At A Glance
              </p>
              <h3 className="font-serif text-2xl text-navy mb-5">
                Your Core Numbers
              </h3>
              <div className="overflow-hidden rounded-xl border border-border">
                <table
                  data-testid="sample-core-numbers-table"
                  className="w-full text-sm"
                >
                  <thead className="bg-navy text-ivory">
                    <tr>
                      <th className="text-left py-3 px-5 font-sans font-semibold">Number</th>
                      <th className="text-left py-3 px-5 font-sans font-semibold">Value</th>
                      <th className="text-left py-3 px-5 font-sans font-semibold">Keyword theme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Life Path", sample.profile.life_path.result],
                      ["Birth Day", sample.profile.birth_day.result],
                      ["Expression / Destiny", sample.profile.expression.result],
                      ["Soul Urge", sample.profile.soul_urge.result],
                      ["Personality", sample.profile.personality.result],
                      ["Personal Year", sample.profile.personal_year.result],
                    ].map(([label, val], i) => (
                      <tr
                        key={label}
                        className={i % 2 === 0 ? "bg-gold-subtle/40" : "bg-white"}
                      >
                        <td className="py-3 px-5 text-slate-700">{label}</td>
                        <td className="py-3 px-5 font-serif text-navy text-lg font-semibold">
                          {val}
                        </td>
                        <td className="py-3 px-5 text-slate-500 italic">
                          {KEYWORDS[val] || "Theme"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Section snippet */}
              <div className="mt-10">
                <p className="eyebrow text-gold tracking-widest">Part 03</p>
                <h3 className="font-serif text-2xl text-navy mt-1 mb-2">
                  Individual Number Interpretations
                </h3>
                <div className="w-10 h-px bg-gold mb-4"></div>
                <p className="italic text-slate-700 leading-relaxed mb-3">
                  Life Path Number is {sample.profile.life_path.result}. Birth
                  Day Number is {sample.profile.birth_day.result}. Expression or
                  Destiny Number is {sample.profile.expression.result}. Soul Urge
                  Number is {sample.profile.soul_urge.result}. Personality Number
                  is {sample.profile.personality.result}.
                </p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  In your full report, each number receives several thoughtful
                  paragraphs — meaningful strengths, balanced growth areas, and
                  gentle reflection prompts — written specifically around your
                  numbers and the areas you tell us matter to you.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Package structure preview tabs */}
        <div className="mt-14">
          <h3 className="font-serif text-3xl text-navy text-center mb-2">
            What's inside each package
          </h3>
          <p className="text-slate-600 text-center text-sm mb-8">
            Pick a package to see the exact chapters we write.
          </p>

          <div
            data-testid="package-tabs"
            className="flex flex-wrap justify-center gap-2 mb-8"
          >
            {Object.entries(PACKAGE_PREVIEWS).map(([key, p]) => (
              <button
                key={key}
                data-testid={`package-tab-${key}`}
                onClick={() => setActive(key)}
                className={`px-5 py-2.5 rounded-full border text-sm transition ${
                  active === key
                    ? "bg-navy text-ivory border-navy"
                    : "bg-white text-slate-700 border-border hover:border-gold"
                }`}
              >
                {p.title}
              </button>
            ))}
          </div>

          <div
            data-testid={`package-preview-${active}`}
            className="bg-white rounded-2xl border border-border p-8 shadow-sm"
          >
            <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold-subtle flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <h4 className="font-serif text-2xl text-navy">{pkg.title}</h4>
                  <p className="text-slate-500 text-sm">{pkg.pages} · {pkg.price}</p>
                </div>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6">
              {pkg.description}
            </p>
            <div className="border-t border-border pt-5">
              <p className="eyebrow text-gold tracking-widest mb-3">Chapters in order</p>
              <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                {pkg.sections.map((s, i) => (
                  <li
                    key={s}
                    className="flex items-start gap-3 text-slate-700 text-sm"
                  >
                    <span className="font-serif text-gold font-semibold w-6 flex-shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/pricing"
            data-testid="sample-report-cta"
            className="btn-gold"
          >
            Create my report <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
